import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { JsonLd, organizationSchema, websiteSchema, serviceSchema, faqSchema } from '../../utils/seo';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  
  // Conversations prédéfinies
  const conversations = [
    [
      { type: 'assistant', text: 'Bonjour, bienvenue au restaurant Le Gourmet.\nNous avons bien reçu votre demande 🤝\nMerci de confirmer que nous pouvons y répondre via WhatsApp.' },
      { type: 'user', text: 'Oui' },
      { type: 'assistant', text: 'Parfait! Nous avons bien noté que vous souhaitez modifier votre réservation de ce soir en passant de 20h à 21h30. Avez-vous des précisions à ajouter ?' },
      { type: 'user', text: 'Non c\'est tout.' },
      { type: 'assistant', text: 'Merci, votre demande a été transmise au restaurant.\nVous recevrez une réponse dès que possible 🙏' }
    ],
    [
      { type: 'assistant', text: 'Bonjour, bienvenue au restaurant Le Gourmet.\nNous avons bien reçu votre demande 🤝\nMerci de confirmer que nous pouvons y répondre via WhatsApp.' },
      { type: 'user', text: 'Oui' },
      { type: 'assistant', text: 'Parfait! Nous avons bien noté votre demande concernant un parapluie rouge que vous avez oublié ce midi. Avez-vous des précisions à rajouter ?' },
      { type: 'user', text: 'Non c\'est tout.' },
      { type: 'assistant', text: 'Bonne nouvelle 😊\nLe restaurant a bien retrouvé votre parapluie rouge.\nVous pouvez dès maintenant venir le récupérer directement au restaurant ☂️✨\n\nÀ très bientôt !' }
    ],
    [
      { type: 'assistant', text: 'Bonjour, bienvenue au restaurant Le Gourmet.\nNous avons bien reçu votre demande 🤝.\nMerci de confirmer que nous pouvons y répondre via WhatsApp.' },
      { type: 'user', text: 'Oui' },
      { type: 'assistant', text: 'Parfait ! 😊 Voici les détails de votre réservation :\n\n📅 Date : vendredi 5 décembre 2025\n🕰️ Heure : 20h30\n👥 Couverts : 4\n👤 Nom : Gabriel\n\nPouvez-vous confirmer que ces informations sont correctes ✅ ?' },
      { type: 'user', text: 'Oui, c\'est exact !' },
      { type: 'assistant', text: 'Parfait ✅. Avez-vous une demande particulière à ajouter ?\n(ex : terrasse, poussette, allergies, chaise bébé…)' },
      { type: 'user', text: 'Non merci' },
      { type: 'assistant', text: 'Merci ! Votre demande a été transmise au restaurant.\nVous recevrez une confirmation dès que possible 🙏' }
    ]
  ];

  const [currentConversationIndex, setCurrentConversationIndex] = useState(0);
  const [chatMessages, setChatMessages] = useState<Array<{ type: string; text: string }>>([]);
  const [messageIndex, setMessageIndex] = useState(0);
  const [showTyping, setShowTyping] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Animation des messages avec défilement automatique dans le téléphone
  useEffect(() => {
    if (messageIndex < conversations[currentConversationIndex].length) {
      setShowTyping(true);
      
      const typingTimer = setTimeout(() => {
        setShowTyping(false);
        setChatMessages(conversations[currentConversationIndex].slice(0, messageIndex + 1));
        setMessageIndex(messageIndex + 1);
        
        // Défilement automatique vers le bas dans le téléphone
        setTimeout(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          }
        }, 100);
      }, 2000);
      
      return () => clearTimeout(typingTimer);
    } else {
      const timer = setTimeout(() => {
        const nextIndex = (currentConversationIndex + 1) % conversations.length;
        setCurrentConversationIndex(nextIndex);
        setMessageIndex(0);
        setChatMessages([]);
        setShowTyping(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [messageIndex, currentConversationIndex]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    restaurant: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);

  // Animation pour les cartes de fonctionnalités - réinitialisée à chaque fois
  const [visibleFeatures, setVisibleFeatures] = useState<number[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = parseInt(entry.target.getAttribute('data-feature-index') || '0');
          if (entry.isIntersecting) {
            setTimeout(() => {
              setVisibleFeatures((prev) => {
                if (!prev.includes(index)) {
                  return [...prev, index];
                }
                return prev;
              });
            }, index * 150);
          } else {
            // Retirer l'index quand l'élément n'est plus visible
            setVisibleFeatures((prev) => prev.filter(i => i !== index));
          }
        });
      },
      { threshold: 0.1 }
    );

    const featureCards = document.querySelectorAll('.feature-card');
    featureCards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  // Gestion du scroll pour la navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Gestion du formulaire de contact
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const form = e.target as HTMLFormElement;
    const formDataToSend = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d51te5hn1516q9jm3sjg', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any)
      });
      
      if (response.ok) {
        setFormSubmitted(true);
        setTimeout(() => {
          setFormSubmitted(false);
          setFormData({ name: '', email: '', phone: '', restaurant: '', message: '' });
        }, 3000);
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du formulaire:', error);
    }
  };

  const handleDemoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowCallModal(true);
  };

  const faqs = [
    {
      question: 'Comment fonctionne Voicely ?',
      answer: 'Lorsqu\'un client appelle et que vous ne pouvez pas répondre, il laisse un message vocal. Notre IA analyse instantanément ce message, comprend la demande et répond au client directement via WhatsApp en quelques secondes. Vous gardez le contrôle total depuis votre tableau de bord.'
    },
    {
      question: 'Voicely est-il disponible 24h/24 ?',
      answer: 'Oui, Voicely fonctionne 24h/24 et 7j/7. Votre assistant IA ne dort jamais et peut gérer les demandes de vos clients à tout moment, même en dehors de vos horaires d\'ouverture.'
    },
    {
      question: 'Quels types d\'établissements peuvent utiliser Voicely ?',
      answer: 'Voicely est conçu spécialement pour les restaurants, bars, clubs et lieux festifs. Notre solution s\'adapte parfaitement aux besoins de la restauration et de l\'hôtellerie.'
    },
    {
      question: 'Voicely peut-il communiquer en plusieurs langues ?',
      answer: 'Oui, votre assistant communique avec vos clients par téléphone et WhatsApp en anglais ou en français, s\'adaptant automatiquement à la langue de votre client.'
    },
    {
      question: 'Comment puis-je personnaliser Voicely pour mon établissement ?',
      answer: 'Voicely est entièrement personnalisable en quelques clics. Vous pouvez adapter l\'assistant à votre image, vos processus et vos besoins spécifiques. De plus, Voicely se connecte à votre plateforme de gestion habituelle (ex. Zenchef, TheFork, ou tout autre système de réservation) afin de récupérer et mettre à jour les informations en temps réel.'
    }
  ];

  return (
    <>
      {/* Schema.org JSON-LD */}
      <JsonLd data={[organizationSchema, websiteSchema, serviceSchema, faqSchema]} />

      <div className="min-h-screen bg-white">
        {/* Call Modal */}
        {showCallModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={() => setShowCallModal(false)}>
            <div className="bg-white rounded-3xl p-5 sm:p-8 lg:p-10 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full bg-gradient-to-br from-[#00B4D8] to-[#0096C7] flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-lg shadow-[#00B4D8]/40">
                  <i className="ri-phone-line text-white text-3xl sm:text-4xl lg:text-5xl"></i>
                </div>
                
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#1A1A1A] mb-2 sm:mb-3">Essayez la démo</h3>
                <p className="text-sm sm:text-base lg:text-lg text-[#1A1A1A] mb-4 sm:mb-6">
                  Appelez notre numéro de démonstration pour tester Voicely en direct
                </p>
                
                <a 
                  href="tel:+33159168804"
                  className="inline-flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-[#00B4D8] to-[#0096C7] text-white rounded-full text-base sm:text-lg font-medium hover:scale-105 hover:shadow-xl hover:shadow-[#00B4D8]/40 transition-all whitespace-nowrap cursor-pointer mb-3 sm:mb-4"
                >
                  <i className="ri-phone-fill text-xl sm:text-2xl"></i>
                  <span>+33 1 59 16 88 04</span>
                </a>
                
                <p className="text-[#1A1A1A] text-xs sm:text-sm mb-4 sm:mb-6">
                  Laissez un message vocal et découvrez comment notre IA répond instantanément
                </p>
                
                <button
                  onClick={() => setShowCallModal(false)}
                  className="text-[#666] hover:text-[#00B4D8] text-xs sm:text-sm font-medium transition-colors cursor-pointer"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
        }`}>
          <div className="w-full px-4 sm:px-6 lg:px-8 h-14 sm:h-16 lg:h-[70px] flex items-center justify-between">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <img 
                src="https://static.readdy.ai/image/2ff91b078c346dc7f0835d5078a50dee/3413903ed85e4cb4622c01579790ab13.png" 
                alt="Voicely Logo" 
                className="h-7 sm:h-8 lg:h-10 w-auto object-contain"
              />
              <span className="text-sm sm:text-base lg:text-xl font-semibold text-[#1A1A1A]">Voicely</span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-10">
              <a href="#accueil" className="text-[#333] hover:text-[#00B4D8] transition-colors relative group">
                Accueil
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#00B4D8] opacity-0 group-hover:opacity-100 transition-opacity"></span>
              </a>
              <a href="#demo" className="text-[#333] hover:text-[#00B4D8] transition-colors">
                Démo
              </a>
              <a href="#fonctionnalites" className="text-[#333] hover:text-[#00B4D8] transition-colors">
                Fonctionnalités
              </a>
              <a href="#apropos" className="text-[#333] hover:text-[#00B4D8] transition-colors">
                À propos
              </a>
              <a href="#faq" className="text-[#333] hover:text-[#00B4D8] transition-colors">
                FAQ
              </a>
              <a href="#contact" className="text-[#333] hover:text-[#00B4D8] transition-colors">
                Contact
              </a>
            </div>

            <button 
              onClick={handleDemoClick}
              className="hidden md:flex items-center gap-2 px-5 lg:px-8 py-2.5 lg:py-3.5 bg-gradient-to-r from-[#00B4D8] to-[#0096C7] text-white text-xs lg:text-base rounded-full hover:scale-105 hover:shadow-xl hover:shadow-[#00B4D8]/50 transition-all whitespace-nowrap cursor-pointer text-center font-semibold animate-pulse"
            >
              <i className="ri-phone-fill text-base lg:text-lg"></i>
              Essayer la démo
            </button>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden w-9 h-9 flex items-center justify-center text-[#1A1A1A] cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl sm:text-2xl`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden bg-white border-t border-[#E0F2FE] shadow-lg">
              <div className="px-4 py-3 sm:py-4 space-y-2 sm:space-y-3">
                <a href="#accueil" onClick={() => setMobileMenuOpen(false)} className="block py-1.5 sm:py-2 text-sm sm:text-base text-[#333] hover:text-[#00B4D8] transition-colors cursor-pointer">
                  Accueil
                </a>
                <a href="#demo" onClick={() => setMobileMenuOpen(false)} className="block py-1.5 sm:py-2 text-sm sm:text-base text-[#333] hover:text-[#00B4D8] transition-colors cursor-pointer">
                  Démo
                </a>
                <a href="#fonctionnalites" onClick={() => setMobileMenuOpen(false)} className="block py-1.5 sm:py-2 text-sm sm:text-base text-[#333] hover:text-[#00B4D8] transition-colors cursor-pointer">
                  Fonctionnalités
                </a>
                <a href="#apropos" onClick={() => setMobileMenuOpen(false)} className="block py-1.5 sm:py-2 text-sm sm:text-base text-[#333] hover:text-[#00B4D8] transition-colors cursor-pointer">
                  À propos
                </a>
                <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="block py-1.5 sm:py-2 text-sm sm:text-base text-[#333] hover:text-[#00B4D8] transition-colors cursor-pointer">
                  FAQ
                </a>
                <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="block py-1.5 sm:py-2 text-sm sm:text-base text-[#333] hover:text-[#00B4D8] transition-colors cursor-pointer">
                  Contact
                </a>
                <button 
                  onClick={(e) => {
                    setMobileMenuOpen(false);
                    handleDemoClick(e);
                  }}
                  className="flex items-center justify-center gap-2 w-full text-center px-5 py-2.5 sm:px-6 sm:py-3.5 bg-gradient-to-r from-[#00B4D8] to-[#0096C7] text-white text-sm sm:text-base rounded-full hover:shadow-lg hover:shadow-[#00B4D8]/30 transition-all whitespace-nowrap cursor-pointer font-semibold"
                >
                  <i className="ri-phone-fill text-base sm:text-lg"></i>
                  Essayer la démo
                </button>
              </div>
            </div>
          )}
        </nav>

        {/* Hero Section */}
        <section id="accueil" className="min-h-screen flex items-center justify-center pt-16 sm:pt-20 lg:pt-24 pb-10 sm:pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#F0F9FF] via-white to-[#E0F2FE] relative overflow-hidden">
          {/* Logo Voicely en arrière-plan */}
          <div className="absolute inset-0 flex items-center justify-center opacity-30 pointer-events-none">
            <img 
              src="https://static.readdy.ai/image/2ff91b078c346dc7f0835d5078a50dee/3413903ed85e4cb4622c01579790ab13.png" 
              alt="Voicely Background Logo" 
              className="w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] object-contain"
            />
          </div>

          <div className="w-full max-w-6xl mx-auto text-center relative z-10">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-bold text-[#1A1A1A] leading-tight mb-3 sm:mb-4 lg:mb-6 xl:mb-8 px-2">
              Transformez votre service client
              <br />
              <span className="bg-gradient-to-r from-[#00B4D8] to-[#0096C7] bg-clip-text text-transparent">grâce à l'intelligence artificielle</span>
            </h1>
            
            <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-[#1A1A1A] max-w-3xl mx-auto mb-2 sm:mb-3 lg:mb-4 xl:mb-6 px-4">
              Avec Voicely, vos clients laissent un message vocal quand vous ne pouvez pas répondre, et leur demande est gérée instantanément par notre IA
            </p>
            
            <p className="text-xs sm:text-sm lg:text-base xl:text-lg text-[#1A1A1A] mb-6 sm:mb-8 lg:mb-12 xl:mb-20 px-4">
              Automatisez votre gestion et offrez une expérience client exceptionnelle
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2.5 sm:gap-3 lg:gap-4 xl:gap-5 px-4">
              <button 
                onClick={handleDemoClick}
                className="w-full sm:w-auto flex items-center justify-center gap-2 sm:gap-2.5 lg:gap-3 px-8 sm:px-10 lg:px-12 xl:px-16 py-3.5 sm:py-4 lg:py-5 xl:py-6 bg-gradient-to-r from-[#00B4D8] to-[#0096C7] text-white rounded-full text-base sm:text-lg lg:text-xl xl:text-2xl font-bold hover:scale-105 hover:shadow-2xl hover:shadow-[#00B4D8]/50 transition-all whitespace-nowrap cursor-pointer text-center relative overflow-hidden group"
              >
                <span className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></span>
                <i className="ri-phone-fill text-xl sm:text-2xl lg:text-2xl xl:text-3xl relative z-10"></i>
                <span className="relative z-10">Essayer la démo</span>
              </button>
            </div>
          </div>
        </section>

        {/* Demo Section - Comment ça fonctionne */}
        <section id="demo" className="py-10 sm:py-12 lg:py-16 xl:py-24 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="w-full max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 sm:gap-8 lg:gap-12">
              {/* Zone Interactive - Téléphone WhatsApp */}
              <div className="lg:col-span-3 flex justify-center">
                <div className="relative w-full max-w-[380px]">
                  {/* Cadre du téléphone */}
                  <div className="bg-[#1A1A1A] rounded-[50px] p-3 shadow-2xl">
                    {/* Écran du téléphone */}
                    <div className="bg-[#ECE5DD] rounded-[40px] overflow-hidden h-[650px] flex flex-col">
                      {/* Header WhatsApp */}
                      <div className="bg-[#075E54] px-4 py-3 flex items-center gap-3 shadow-md">
                        <button className="w-8 h-8 flex items-center justify-center text-white cursor-pointer">
                          <i className="ri-arrow-left-line text-xl"></i>
                        </button>
                        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border-2 border-white/20">
                          <img
                            src="https://public.readdy.ai/ai/img_res/307f4c23f0647cb709858ac90f81bcc2.jpg"
                            alt="Le Gourmet"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-medium text-base">Le Gourmet</h3>
                          <p className="text-white/70 text-xs">en ligne</p>
                        </div>
                        <div className="flex gap-4">
                          <button className="w-8 h-8 flex items-center justify-center text-white cursor-pointer">
                            <i className="ri-phone-fill text-lg"></i>
                          </button>
                          <button className="w-8 h-8 flex items-center justify-center text-white cursor-pointer">
                            <i className="ri-more-2-fill text-lg"></i>
                          </button>
                        </div>
                      </div>

                      {/* Messages Area - Défilement automatique, pas manuel */}
                      <div 
                        ref={chatContainerRef}
                        className="flex-1 overflow-y-auto px-4 py-4 space-y-2"
                        style={{ 
                          scrollBehavior: 'smooth',
                          overflowY: 'auto',
                          pointerEvents: 'none' // Bloque le défilement manuel
                        }}
                      >
                        {chatMessages.map((msg, index) => (
                          <div key={index} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}>
                            <div className={`max-w-[75%] px-3 py-2 rounded-lg text-sm whitespace-pre-line shadow-sm ${
                              msg.type === 'user' 
                                ? 'bg-[#DCF8C6] text-[#1A1A1A]' 
                                : 'bg-white text-[#1A1A1A]'
                            }`}>
                              {msg.text}
                              <div className="text-[10px] text-gray-500 text-right mt-1">
                                {new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {/* Bulle "typing" */}
                        {showTyping && (
                          <div className="flex justify-start animate-fade-in">
                            <div className="bg-white px-4 py-3 rounded-lg shadow-sm">
                              <div className="flex gap-1">
                                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Input Area (fictive) */}
                      <div className="bg-[#F0F0F0] px-3 py-2 flex items-center gap-2">
                        <button className="w-9 h-9 flex items-center justify-center text-[#54656F] cursor-pointer">
                          <i className="ri-emotion-happy-line text-2xl"></i>
                        </button>
                        <div className="flex-1 bg-white rounded-full px-4 py-2 text-sm text-[#667781]">
                          Message
                        </div>
                        <button className="w-9 h-9 flex items-center justify-center text-[#54656F] cursor-pointer">
                          <i className="ri-attachment-line text-xl"></i>
                        </button>
                        <button className="w-9 h-9 flex items-center justify-center text-white bg-[#00B4D8] rounded-full cursor-pointer">
                          <i className="ri-mic-line text-lg"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Explications */}
              <div className="lg:col-span-2 flex flex-col justify-center">
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#1A1A1A] mb-5 sm:mb-6 lg:mb-8">Comment ça fonctionne ?</h3>
                
                <div className="space-y-5 sm:space-y-6 lg:space-y-8">
                  <div className="flex gap-2.5 sm:gap-3 lg:gap-4">
                    <div className="relative flex-shrink-0">
                      <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#10B981]/20">1</span>
                    </div>
                    <div className="flex-1 pt-0.5 sm:pt-1 lg:pt-2">
                      <div className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center mb-2 sm:mb-2.5 lg:mb-3 shadow-md shadow-[#10B981]/30">
                        <i className="ri-phone-line text-white text-base sm:text-lg"></i>
                      </div>
                      <h4 className="font-semibold text-[#1A1A1A] mb-1.5 sm:mb-2 text-sm sm:text-base">Le client appelle</h4>
                      <p className="text-[#666] text-xs sm:text-sm leading-relaxed">
                        S'il vous contacte et que vous ne pouvez pas répondre, le client laisse un message vocal sur votre ligne.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2.5 sm:gap-3 lg:gap-4">
                    <div className="relative flex-shrink-0">
                      <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#25D366]/20">2</span>
                    </div>
                    <div className="flex-1 pt-0.5 sm:pt-1 lg:pt-2">
                      <div className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full bg-[#25D366] flex items-center justify-center mb-2 sm:mb-2.5 lg:mb-3 shadow-md shadow-[#25D366]/30">
                        <i className="ri-whatsapp-line text-white text-base sm:text-lg"></i>
                      </div>
                      <h4 className="font-semibold text-[#1A1A1A] mb-1.5 sm:mb-2 text-sm sm:text-base">L'IA répond instantanément via WhatsApp</h4>
                      <p className="text-[#666] text-xs sm:text-sm leading-relaxed">
                        L'assistant analyse le message vocal, comprend la demande et répond au client directement sur WhatsApp en quelques secondes.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2.5 sm:gap-3 lg:gap-4">
                    <div className="relative flex-shrink-0">
                      <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#00B4D8]/20">3</span>
                    </div>
                    <div className="flex-1 pt-0.5 sm:pt-1 lg:pt-2">
                      <div className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full overflow-hidden mb-2 sm:mb-2.5 lg:mb-3 shadow-md shadow-[#00B4D8]/30 flex items-center justify-center">
                        <img 
                          src="https://static.readdy.ai/image/2ff91b078c346dc7f0835d5078a50dee/97f0bcff15cce7e37e11fa7d8c3196d0.png" 
                          alt="Voicely" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <h4 className="font-semibold text-[#1A1A1A] mb-1.5 sm:mb-2 text-sm sm:text-base">Voicely traite la demande</h4>
                      <p className="text-[#666] text-xs sm:text-sm leading-relaxed">
                        Qu'il s'agisse d'une réservation, d'une modification, d'une question ou d'une annulation, Voicely gère l'action automatiquement et le client est tenu informé en temps réel sur WhatsApp.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2.5 sm:gap-3 lg:gap-4">
                    <div className="relative flex-shrink-0">
                      <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F59E0B]/20">4</span>
                    </div>
                    <div className="flex-1 pt-0.5 sm:pt-1 lg:pt-2">
                      <div className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full bg-gradient-to-br from-[#F59E0B] to-[#D97706] flex items-center justify-center mb-2 sm:mb-2.5 lg:mb-3 shadow-md shadow-[#F59E0B]/30">
                        <i className="ri-dashboard-line text-white text-base sm:text-lg"></i>
                      </div>
                      <h4 className="font-semibold text-[#1A1A1A] mb-1.5 sm:mb-2 text-sm sm:text-base">Vous gardez le contrôle</h4>
                      <p className="text-[#666] text-xs sm:text-sm leading-relaxed">
                        Depuis votre tableau de bord, vous suivez toutes les interactions, validez les actions importantes et gardez la main sur l'ensemble de votre activité.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="fonctionnalites" className="py-10 sm:py-12 lg:py-16 xl:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A2540] via-[#0D3A5F] to-[#0A2540] relative overflow-hidden">
          {/* Motifs décoratifs */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          </div>

          <div className="w-full max-w-7xl mx-auto relative z-10">
            <div className="text-center mb-8 sm:mb-10 lg:mb-12 xl:mb-16">
              <span className="text-[10px] sm:text-xs lg:text-sm font-semibold text-[#00B4D8] uppercase tracking-wide">Fonctionnalités</span>
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-2 sm:mb-3 lg:mb-4">
                Tout ce dont vous avez besoin
                <br />
                <span className="bg-gradient-to-r from-[#00B4D8] to-[#0096C7] bg-clip-text text-transparent">pour automatiser votre activité</span>
              </h2>
              <p className="text-xs sm:text-sm lg:text-base xl:text-lg text-white/80 max-w-2xl mx-auto">
                Des fonctionnalités puissantes conçues pour simplifier votre quotidien et améliorer l'expérience de vos clients
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6 xl:gap-8">
              {/* Feature 1 */}
              <div 
                className={`feature-card bg-white rounded-2xl shadow-md p-4 sm:p-5 lg:p-6 xl:p-8 hover:shadow-xl hover:-translate-y-2 transition-all duration-500 cursor-pointer border border-[#E0F2FE] group ${
                  visibleFeatures.includes(0) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                data-feature-index="0"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full bg-gradient-to-br from-[#00B4D8] to-[#0096C7] flex items-center justify-center mb-3 sm:mb-4 lg:mb-6 shadow-lg shadow-[#00B4D8]/30 group-hover:scale-110 transition-transform">
                  <i className="ri-calendar-check-line text-white text-xl sm:text-2xl lg:text-3xl"></i>
                </div>
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-[#1A1A1A] mb-2 sm:mb-3 lg:mb-4">Prise de réservation automatique</h3>
                <p className="text-[#666] text-xs sm:text-sm lg:text-base leading-relaxed mb-3 sm:mb-4 lg:mb-6">
                  Votre assistant gère les réservations 24h/24, vérifie les disponibilités et confirme instantanément les demandes de vos clients.
                </p>
                <a href="#contact" className="text-[10px] sm:text-xs lg:text-sm text-[#00B4D8] font-medium hover:underline cursor-pointer">
                  En savoir plus →
                </a>
              </div>

              {/* Feature 2 */}
              <div 
                className={`feature-card bg-white rounded-2xl shadow-md p-4 sm:p-5 lg:p-6 xl:p-8 hover:shadow-xl hover:-translate-y-2 transition-all duration-500 cursor-pointer border border-[#E0F2FE] group ${
                  visibleFeatures.includes(1) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                data-feature-index="1"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full bg-gradient-to-br from-[#00B4D8] to-[#0096C7] flex items-center justify-center mb-3 sm:mb-4 lg:mb-6 shadow-lg shadow-[#00B4D8]/30 group-hover:scale-110 transition-transform">
                  <i className="ri-customer-service-2-line text-white text-xl sm:text-2xl lg:text-3xl"></i>
                </div>
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-[#1A1A1A] mb-2 sm:mb-3 lg:mb-4">Réponses instantanées aux clients</h3>
                <p className="text-[#666] text-xs sm:text-sm lg:text-base leading-relaxed mb-3 sm:mb-4 lg:mb-6">
                  Répondez aux questions fréquentes, informez sur vos horaires, votre menu et vos services sans délai d'attente.
                </p>
                <a href="#contact" className="text-[10px] sm:text-xs lg:text-sm text-[#00B4D8] font-medium hover:underline cursor-pointer">
                  En savoir plus →
                </a>
              </div>

              {/* Feature 3 */}
              <div 
                className={`feature-card bg-white rounded-2xl shadow-md p-4 sm:p-5 lg:p-6 xl:p-8 hover:shadow-xl hover:-translate-y-2 transition-all duration-500 cursor-pointer border border-[#E0F2FE] group ${
                  visibleFeatures.includes(2) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                data-feature-index="2"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full bg-gradient-to-br from-[#00B4D8] to-[#0096C7] flex items-center justify-center mb-3 sm:mb-4 lg:mb-6 shadow-lg shadow-[#00B4D8]/30 group-hover:scale-110 transition-transform">
                  <i className="ri-notification-3-line text-white text-xl sm:text-2xl lg:text-3xl"></i>
                </div>
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-[#1A1A1A] mb-2 sm:mb-3 lg:mb-4">Rappels et notifications</h3>
                <p className="text-[#666] text-xs sm:text-sm lg:text-base leading-relaxed mb-3 sm:mb-4 lg:mb-6">
                  Envoyez automatiquement des rappels de réservation, des confirmations et des messages de suivi personnalisés.
                </p>
                <a href="#contact" className="text-[10px] sm:text-xs lg:text-sm text-[#00B4D8] font-medium hover:underline cursor-pointer">
                  En savoir plus →
                </a>
              </div>

              {/* Feature 4 */}
              <div 
                className={`feature-card bg-white rounded-2xl shadow-md p-4 sm:p-5 lg:p-6 xl:p-8 hover:shadow-xl hover:-translate-y-2 transition-all duration-500 cursor-pointer border border-[#E0F2FE] group ${
                  visibleFeatures.includes(3) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                data-feature-index="3"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full bg-gradient-to-br from-[#00B4D8] to-[#0096C7] flex items-center justify-center mb-3 sm:mb-4 lg:mb-6 shadow-lg shadow-[#00B4D8]/30 group-hover:scale-110 transition-transform">
                  <i className="ri-bar-chart-box-line text-white text-xl sm:text-2xl lg:text-3xl"></i>
                </div>
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-[#1A1A1A] mb-2 sm:mb-3 lg:mb-4">Statistiques et analyses</h3>
                <p className="text-[#666] text-xs sm:text-sm lg:text-base leading-relaxed mb-3 sm:mb-4 lg:mb-6">
                  Suivez en temps réel le nombre d'appels pris en charge par Voicely, visualisez les demandes traitées et mesurez concrètement les opportunités que vous n'avez pas manquées.
                </p>
                <a href="#contact" className="text-[10px] sm:text-xs lg:text-sm text-[#00B4D8] font-medium hover:underline cursor-pointer">
                  En savoir plus →
                </a>
              </div>

              {/* Feature 5 */}
              <div 
                className={`feature-card bg-white rounded-2xl shadow-md p-4 sm:p-5 lg:p-6 xl:p-8 hover:shadow-xl hover:-translate-y-2 transition-all duration-500 cursor-pointer border border-[#E0F2FE] group ${
                  visibleFeatures.includes(4) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                data-feature-index="4"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full bg-gradient-to-br from-[#00B4D8] to-[#0096C7] flex items-center justify-center mb-3 sm:mb-4 lg:mb-6 shadow-lg shadow-[#00B4D8]/30 group-hover:scale-110 transition-transform">
                  <i className="ri-global-line text-white text-xl sm:text-2xl lg:text-3xl"></i>
                </div>
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-[#1A1A1A] mb-2 sm:mb-3 lg:mb-4">Bilingue</h3>
                <p className="text-[#666] text-xs sm:text-sm lg:text-base leading-relaxed mb-3 sm:mb-4 lg:mb-6">
                  Votre assistant communique avec vos clients par téléphone et WhatsApp en anglais ou en français.
                </p>
                <a href="#contact" className="text-[10px] sm:text-xs lg:text-sm text-[#00B4D8] font-medium hover:underline cursor-pointer">
                  En savoir plus →
                </a>
              </div>

              {/* Feature 6 */}
              <div 
                className={`feature-card bg-white rounded-2xl shadow-md p-4 sm:p-5 lg:p-6 xl:p-8 hover:shadow-xl hover:-translate-y-2 transition-all duration-500 cursor-pointer border border-[#E0F2FE] group ${
                  visibleFeatures.includes(5) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                data-feature-index="5"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full bg-gradient-to-br from-[#00B4D8] to-[#0096C7] flex items-center justify-center mb-3 sm:mb-4 lg:mb-6 shadow-lg shadow-[#00B4D8]/30 group-hover:scale-110 transition-transform">
                  <i className="ri-settings-3-line text-white text-xl sm:text-2xl lg:text-3xl"></i>
                </div>
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-[#1A1A1A] mb-2 sm:mb-3 lg:mb-4">Personnalisation complète</h3>
                <p className="text-[#666] text-xs sm:text-sm lg:text-base leading-relaxed mb-3 sm:mb-4 lg:mb-6">
                  Adaptez l'assistant à votre image, à vos processus et à vos besoins spécifiques. Voicely se connecte à votre plateforme de gestion habituelle (ex. Zenchef, TheFork, ou tout autre système de réservation) afin de récupérer et mettre à jour les informations en temps réel.
                </p>
                <a href="#contact" className="text-[10px] sm:text-xs lg:text-sm text-[#00B4D8] font-medium hover:underline cursor-pointer">
                  En savoir plus →
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="apropos" className="py-10 sm:py-12 lg:py-16 xl:py-24 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="w-full max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-16 items-center">
              {/* Image */}
              <div className="relative order-2 lg:order-1">
                <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-[#E0F2FE]">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20restaurant%20interior%20with%20elegant%20design%2C%20professional%20staff%20serving%20customers%20with%20tablets%20and%20technology%2C%20warm%20ambient%20lighting%2C%20contemporary%20furniture%2C%20sophisticated%20atmosphere%2C%20high-end%20dining%20experience%2C%20clean%20minimalist%20aesthetic&width=600&height=700&seq=about-image-1&orientation=portrait"
                    alt="Restaurant moderne"
                    className="w-full h-[300px] sm:h-[350px] lg:h-[450px] xl:h-[700px] object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-3 -right-3 sm:-bottom-4 sm:-right-4 lg:-bottom-8 lg:-right-8 w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32 xl:w-48 xl:h-48 bg-gradient-to-br from-[#00B4D8]/20 to-[#0096C7]/10 rounded-3xl -z-10"></div>
              </div>

              {/* Content */}
              <div className="order-1 lg:order-2">
                <span className="text-[10px] sm:text-xs lg:text-sm font-semibold text-[#00B4D8] uppercase tracking-wide">Notre Mission</span>
                <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-[#1A1A1A] mt-2 sm:mt-3 lg:mt-4 mb-3 sm:mb-4 lg:mb-6">
                  Pourquoi Voicely existe
                </h2>
                
                <p className="text-sm sm:text-base md:text-lg lg:text-xl text-[#333] leading-relaxed mb-3 sm:mb-4">
                  Nous avons créé Voicely pour vous aider à gérer les appels lorsque vous ne pouvez pas répondre. Aux heures de forte activité, il est parfois difficile de prendre le téléphone tout en s'occupant des personnes présentes sur place.
                </p>

                <p className="text-sm sm:text-base md:text-lg text-[#333] leading-relaxed mb-3 sm:mb-4">
                  Lorsqu'un appel est manqué, le message vocal est automatiquement traité par notre IA et la demande est transmise via WhatsApp. Qu'il s'agisse d'une question, d'une prise de contact ou de toute autre demande, Voicely assure la continuité sans interruption.
                </p>

                <p className="text-sm sm:text-base lg:text-lg text-[#333] leading-relaxed mb-6 sm:mb-8 lg:mb-10">
                  Vous gardez le contrôle à tout moment : vous choisissez comment et quand répondre, tandis que Voicely se charge de ne rien laisser passer.
                </p>

                {/* Stats - Mise à jour */}
                <div className="grid grid-cols-1 gap-3 sm:gap-4 lg:gap-8">
                  <div className="text-center p-2.5 sm:p-3 lg:p-4 bg-gradient-to-br from-[#F0F9FF] to-[#E0F2FE] rounded-xl">
                    <div className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold mb-1 sm:mb-1.5 lg:mb-2" style={{ color: '#0066FF' }}>24/7</div>
                    <div className="text-[10px] sm:text-xs lg:text-sm text-[#1A1A1A]">Disponibilité continue</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Target Section - Restaurants & Bars uniquement */}
        <section className="py-10 sm:py-12 lg:py-16 xl:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A2540] via-[#0D3A5F] to-[#0A2540] overflow-hidden relative">
          {/* Motifs décoratifs */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          </div>

          <div className="w-full max-w-5xl mx-auto relative z-10">
            <div className="text-center mb-8 sm:mb-10 lg:mb-12 xl:mb-16">
              <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-white mb-2 sm:mb-3 lg:mb-4">
                Spécialement conçu pour les professionnels
                <br />
                <span className="bg-gradient-to-r from-[#00B4D8] to-[#0096C7] bg-clip-text text-transparent">de la restauration et des lieux festifs</span>
              </h2>
              <p className="text-sm sm:text-base lg:text-lg text-white/80 max-w-3xl mx-auto">
                Une solution sur mesure pour gérer efficacement vos réservations et satisfaire vos clients
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 lg:gap-8">
              {/* Restaurants */}
              <div className="bg-white rounded-3xl p-5 sm:p-6 lg:p-8 xl:p-10 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2">
                <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full bg-gradient-to-br from-[#00B4D8] to-[#0096C7] flex items-center justify-center mx-auto mb-4 sm:mb-5 lg:mb-6 shadow-lg shadow-[#00B4D8]/40">
                  <i className="ri-restaurant-2-line text-white text-2xl sm:text-3xl lg:text-4xl"></i>
                </div>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#1A1A1A] text-center mb-2 sm:mb-3 lg:mb-4">Restaurants</h3>
                <p className="text-xs sm:text-sm lg:text-base text-[#666] text-center leading-relaxed">
                  Optimisez la gestion de vos réservations et offrez une expérience client irréprochable, même lors des services les plus chargés.
                </p>
              </div>

              {/* Bars, Clubs et Lieux festifs */}
              <div className="bg-white rounded-3xl p-5 sm:p-6 lg:p-8 xl:p-10 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2">
                <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full bg-gradient-to-br from-[#00B4D8] to-[#0096C7] flex items-center justify-center mx-auto mb-4 sm:mb-5 lg:mb-6 shadow-lg shadow-[#00B4D8]/40">
                  <i className="ri-goblet-line text-white text-2xl sm:text-3xl lg:text-4xl"></i>
                </div>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#1A1A1A] text-center mb-2 sm:mb-3 lg:mb-4">Bars, Clubs et Lieux festifs</h3>
                <p className="text-xs sm:text-sm lg:text-base text-[#666] text-center leading-relaxed">
                  Gérez les demandes de privatisation, les réservations de tables et les questions de vos clients sans interruption.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="py-10 sm:py-12 lg:py-16 xl:py-24 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="w-full max-w-4xl mx-auto">
            <div className="text-center mb-8 sm:mb-10 lg:mb-12 xl:mb-16">
              <span className="text-[10px] sm:text-xs lg:text-sm font-semibold text-[#00B4D8] uppercase tracking-wide">FAQ</span>
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-[#1A1A1A] mt-2 sm:mt-3 lg:mt-4 mb-2 sm:mb-3 lg:mb-4 px-2">
                Questions fréquentes
              </h2>
              <p className="text-xs sm:text-sm lg:text-base xl:text-lg text-[#666] max-w-2xl mx-auto px-4">
                Tout ce que vous devez savoir sur Voicely
              </p>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-[#F0F9FF] to-[#E0F2FE] rounded-2xl border border-[#00B4D8]/20 overflow-hidden shadow-sm hover:shadow-md transition-all"
                >
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full px-4 sm:px-5 lg:px-6 xl:px-8 py-3 sm:py-4 lg:py-5 xl:py-6 flex items-center justify-between text-left cursor-pointer hover:bg-white/50 transition-colors"
                  >
                    <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-[#1A1A1A] pr-3 sm:pr-4">
                      {faq.question}
                    </h3>
                    <div className={`w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0 rounded-full bg-gradient-to-br from-[#00B4D8] to-[#0096C7] flex items-center justify-center transition-transform ${
                      openFaqIndex === index ? 'rotate-180' : ''
                    }`}>
                      <i className="ri-arrow-down-s-line text-white text-lg sm:text-xl"></i>
                    </div>
                  </button>
                  
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openFaqIndex === index ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    <div className="px-4 sm:px-5 lg:px-6 xl:px-8 pb-3 sm:pb-4 lg:pb-5 xl:pb-6">
                      <p className="text-xs sm:text-sm lg:text-base text-[#333] leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-6 sm:mt-8 lg:mt-10 xl:mt-12">
              <p className="text-xs sm:text-sm lg:text-base text-[#666] mb-3 sm:mb-4">
                Vous avez d'autres questions ?
              </p>
              <a
                href="#contact"
                className="inline-flex items-center gap-1.5 sm:gap-2 px-5 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 bg-gradient-to-r from-[#00B4D8] to-[#0096C7] text-white rounded-full text-xs sm:text-sm lg:text-base font-medium hover:scale-105 hover:shadow-xl hover:shadow-[#00B4D8]/40 transition-all whitespace-nowrap cursor-pointer"
              >
                Contactez-nous
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-10 sm:py-12 lg:py-16 xl:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#00B4D8] via-[#0096C7] to-[#0077B6] relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          </div>

          <div className="w-full max-w-3xl mx-auto relative z-10">
            <div className="text-center mb-6 sm:mb-8 lg:mb-12">
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-2 sm:mb-3 lg:mb-4 px-2">
                Prêt à transformer votre restaurant ?
              </h2>
              <p className="text-sm sm:text-base lg:text-lg xl:text-xl text-white/90 px-4">
                Contactez-nous pour une démo personnalisée
              </p>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-3xl p-5 sm:p-6 lg:p-8 xl:p-12 shadow-2xl">
              {formSubmitted ? (
                <div className="text-center py-6 sm:py-8 lg:py-12">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full bg-gradient-to-br from-[#00B4D8] to-[#0096C7] flex items-center justify-center mx-auto mb-3 sm:mb-4 lg:mb-6 shadow-lg shadow-[#00B4D8]/40">
                    <i className="ri-check-line text-white text-2xl sm:text-3xl lg:text-4xl"></i>
                  </div>
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#1A1A1A] mb-1.5 sm:mb-2 lg:mb-3">Message envoyé !</h3>
                  <p className="text-[#666] text-xs sm:text-sm lg:text-base">Nous vous répondrons dans les plus brefs délais.</p>
                </div>
              ) : (
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 lg:space-y-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-[#333] mb-1.5 sm:mb-2">
                      Nom complet <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 sm:px-4 lg:px-5 py-2.5 sm:py-3 lg:py-4 bg-[#F0F9FF] border border-[#E0F2FE] rounded-lg text-[#1A1A1A] text-xs sm:text-sm lg:text-base focus:outline-none focus:border-[#00B4D8] focus:ring-2 focus:ring-[#00B4D8]/20 transition-all"
                      placeholder="Jean Dupont"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-[#333] mb-1.5 sm:mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 sm:px-4 lg:px-5 py-2.5 sm:py-3 lg:py-4 bg-[#F0F9FF] border border-[#E0F2FE] rounded-lg text-[#1A1A1A] text-xs sm:text-sm lg:text-base focus:outline-none focus:border-[#00B4D8] focus:ring-2 focus:ring-[#00B4D8]/20 transition-all"
                      placeholder="jean.dupont@restaurant.fr"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-[#333] mb-1.5 sm:mb-2">Numéro de téléphone <span className="text-red-500">*</span></label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 sm:px-4 lg:px-5 py-2.5 sm:py-3 lg:py-4 bg-[#F0F9FF] border border-[#E0F2FE] rounded-lg text-[#1A1A1A] text-xs sm:text-sm lg:text-base focus:outline-none focus:border-[#00B4D8] focus:ring-2 focus:ring-[#00B4D8]/20 transition-all"
                      placeholder="+33 6 12 34 56 78"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-[#333] mb-1.5 sm:mb-2">Nom du restaurant <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="restaurant"
                      required
                      value={formData.restaurant}
                      onChange={(e) => setFormData({ ...formData, restaurant: e.target.value })}
                      className="w-full px-3 sm:px-4 lg:px-5 py-2.5 sm:py-3 lg:py-4 bg-[#F0F9FF] border border-[#E0F2FE] rounded-lg text-[#1A1A1A] text-xs sm:text-sm lg:text-base focus:outline-none focus:border-[#00B4D8] focus:ring-2 focus:ring-[#00B4D8]/20 transition-all"
                      placeholder="Le Gourmet Parisien"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-[#333] mb-1.5 sm:mb-2">Message</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={4}
                      className="w-full px-3 sm:px-4 lg:px-5 py-2.5 sm:py-3 lg:py-4 bg-[#F0F9FF] border border-[#E0F2FE] rounded-lg text-[#1A1A1A] text-xs sm:text-sm lg:text-base focus:outline-none focus:border-[#00B4D8] focus:ring-2 focus:ring-[#00B4D8]/20 transition-all resize-none"
                      placeholder="Parlez-nous de votre projet et de vos besoins..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-2.5 sm:py-3 lg:py-4 bg-gradient-to-r from-[#00B4D8] to-[#0096C7] text-white rounded-full text-sm sm:text-base lg:text-lg font-medium hover:scale-[1.02] hover:shadow-xl hover:shadow-[#00B4D8]/40 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Envoyer ma demande
                    <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                </form>
              )}
            </div>

            <p className="text-center text-white/80 text-[10px] sm:text-xs lg:text-sm mt-5 sm:mt-6 lg:mt-8 px-4">
              Ou contactez-nous directement : <a href="mailto:voicely.ia@gmail.com" className="text-white hover:underline cursor-pointer">voicely.ia@gmail.com</a>
            </p>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#1A1A1A] rounded-t-[30px] pt-8 sm:pt-10 lg:pt-12 xl:pt-16 pb-5 sm:pb-6 lg:pb-8 px-4 sm:px-6 lg:px-8">
          <div className="w-full max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10 xl:gap-12 mb-6 sm:mb-8 lg:mb-12">
              {/* Column 1 - Brand */}
              <div>
                <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-3 mb-2.5 sm:mb-3 lg:mb-4">
                  <img 
                    src="https://static.readdy.ai/image/2ff91b078c346dc7f0835d5078a50dee/3413903ed85e4cb4622c01579790ab13.png" 
                    alt="Voicely Logo" 
                    className="h-7 sm:h-8 lg:h-10 w-auto object-contain"
                  />
                  <span className="text-base sm:text-lg lg:text-xl font-semibold text-white">Voicely</span>
                </div>
                <p className="text-white/70 text-[10px] sm:text-xs lg:text-sm leading-relaxed">
                  Vos clients laissent un message vocal, notre IA gère leur demande instantanément.
                </p>
              </div>

              {/* Column 2 - Navigation */}
              <div>
                <h4 className="text-white font-semibold mb-2.5 sm:mb-3 lg:mb-4 text-xs sm:text-sm lg:text-base">Navigation</h4>
                <div className="space-y-1.5 sm:space-y-2 lg:space-y-3">
                  <a href="#accueil" className="block text-white/70 text-[10px] sm:text-xs lg:text-sm hover:text-[#00B4D8] transition-colors cursor-pointer">Accueil</a>
                  <a href="#demo" className="block text-white/70 text-[10px] sm:text-xs lg:text-sm hover:text-[#00B4D8] transition-colors cursor-pointer">Démo</a>
                  <a href="#fonctionnalites" className="block text-white/70 text-[10px] sm:text-xs lg:text-sm hover:text-[#00B4D8] transition-colors cursor-pointer">Fonctionnalités</a>
                  <a href="#apropos" className="block text-white/70 text-[10px] sm:text-xs lg:text-sm hover:text-[#00B4D8] transition-colors cursor-pointer">À propos</a>
                  <a href="#faq" className="block text-white/70 text-[10px] sm:text-xs lg:text-sm hover:text-[#00B4D8] transition-colors cursor-pointer">FAQ</a>
                  <a href="#contact" className="block text-white/70 text-[10px] sm:text-xs lg:text-sm hover:text-[#00B4D8] transition-colors cursor-pointer">Contact</a>
                </div>
              </div>

              {/* Column 3 - Social */}
              <div>
                <h4 className="text-white font-semibold mb-2.5 sm:mb-3 lg:mb-4 text-xs sm:text-sm lg:text-base">Suivez-nous</h4>
                <div className="flex gap-2.5 sm:gap-3">
                  <a href="https://www.instagram.com/ai.voicely/" rel="nofollow" target="_blank" className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-gradient-to-br hover:from-[#00B4D8] hover:to-[#0096C7] transition-all cursor-pointer">
                    <i className="ri-instagram-line text-white text-xs sm:text-sm lg:text-base"></i>
                  </a>
                  <a href="https://www.linkedin.com/company/voicely-ia/" rel="nofollow" target="_blank" className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-gradient-to-br hover:from-[#00B4D8] hover:to-[#0096C7] transition-all cursor-pointer">
                    <i className="ri-linkedin-line text-white text-xs sm:text-sm lg:text-base"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Bottom */}
            <div className="border-t border-white/10 pt-5 sm:pt-6 lg:pt-8">
              <div className="flex justify-center items-center">
                <p className="text-white/60 text-[10px] sm:text-xs text-center">
                  © 2025 Voicely. Tous droits réservés.
                </p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
